/**
 * 表单验证工具函数
 * 根据后端模型定义进行字段验证
 */

/**
 * 学生表单验证
 * @param {Object} formData - 表单数据
 * @returns {Object} { valid: boolean, errors: Array<string> }
 */
export const validateStudentForm = (formData) => {
  const errors = [];

  // 必填字段验证
  if (!formData.name || !formData.name.trim()) {
    errors.push('学生姓名不能为空');
  } else if (formData.name.length > 100) {
    errors.push('学生姓名长度不能超过100个字符');
  }

  if (!formData.gender) {
    errors.push('请选择性别');
  } else if (!['男', '女'].includes(formData.gender)) {
    errors.push('性别只能是"男"或"女"');
  }

  if (!formData.contact || !formData.contact.trim()) {
    errors.push('联系方式不能为空');
  } else if (formData.contact.length > 50) {
    errors.push('联系方式长度不能超过50个字符');
  }

  // 出生年月必填验证（与 PC 端对齐）
  if (!formData.birth_year) {
    errors.push('出生年份不能为空');
  } else {
    const year = parseInt(formData.birth_year);
    const currentYear = new Date().getFullYear();
    if (isNaN(year) || year < 1900 || year > currentYear) {
      errors.push(`出生年份必须在 1900 到 ${currentYear} 年之间`);
    }
  }

  if (!formData.birth_month) {
    errors.push('出生月份不能为空');
  } else {
    const month = parseInt(formData.birth_month);
    if (isNaN(month) || month < 1 || month > 12) {
      errors.push('出生月份必须在 1 到 12 之间');
    }
  }

  if (formData.grade && formData.grade.length > 50) {
    errors.push('年级长度不能超过50个字符');
  }

  if (formData.hobbies && formData.hobbies.length > 255) {
    errors.push('爱好/推荐人长度不能超过255个字符');
  }

  if (formData.student_type && !['在学', '潜在', '已结束'].includes(formData.student_type)) {
    errors.push('学生类型只能是"在学"、"潜在"或"已结束"');
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * 课程表单验证
 * @param {Object} formData - 表单数据
 * @returns {Object} { valid: boolean, errors: Array<string> }
 */
export const validateCourseForm = (formData) => {
  const errors = [];

  if (!formData.name || !formData.name.trim()) {
    errors.push('课程名称不能为空');
  }

  if (formData.status && !['招生中', '进行中', '已结课'].includes(formData.status)) {
    errors.push('课程状态只能是"招生中"、"进行中"或"已结课"');
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * 财务记录表单验证
 * @param {Object} formData - 表单数据
 * @returns {Object} { valid: boolean, errors: Array<string> }
 */
export const validateFinanceForm = (formData) => {
  const errors = [];

  if (!formData.date) {
    errors.push('购买日期不能为空');
  }

  if (!formData.student_id) {
    errors.push('请选择学生');
  }

  if (!formData.course_id) {
    errors.push('请选择课程');
  }

  if (!formData.price) {
    errors.push('实付价不能为空');
  } else {
    const price = parseFloat(formData.price);
    if (isNaN(price) || price < 0) {
      errors.push('实付价必须是大于等于0的数字');
    }
  }

  if (!formData.method) {
    errors.push('请选择支付方式');
  }

  if (!formData.totalHours) {
    errors.push('总课时不能为空');
  } else {
    const hours = parseFloat(formData.totalHours);
    if (isNaN(hours) || hours <= 0) {
      errors.push('总课时必须是大于0的数字');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * 上课记录表单验证
 * @param {Object} formData - 表单数据
 * @returns {Object} { valid: boolean, errors: Array<string> }
 */
export const validateRecordForm = (formData) => {
  const errors = [];

  if (!formData.date) {
    errors.push('上课日期不能为空');
  }

  if (!formData.student_id) {
    errors.push('请选择学生');
  }

  if (!formData.course_id) {
    errors.push('请选择课程');
  }

  if (!formData.consumed_hours) {
    errors.push('消耗课时不能为空');
  } else {
    const hours = parseFloat(formData.consumed_hours);
    if (isNaN(hours) || hours <= 0) {
      errors.push('消耗课时必须是大于0的数字');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * 显示验证错误（使用 alert）
 * @deprecated 建议使用 showValidationErrorsWithToast
 * @param {Array<string>} errors - 错误信息数组
 */
export const showValidationErrors = (errors) => {
  if (errors.length > 0) {
    alert('表单验证失败：\n\n' + errors.join('\n'));
  }
};

/**
 * 显示验证错误（使用 Toast）
 * @param {Array<string>} errors - 错误信息数组
 * @param {Object} toast - Toast 实例（从 useToast() 获取）
 */
export const showValidationErrorsWithToast = (errors, toast) => {
  if (errors.length > 0) {
    // 显示第一个错误
    toast.error(errors[0]);
    
    // 如果有多个错误，在控制台显示完整列表
    if (errors.length > 1) {
      console.warn('表单验证错误：', errors);
    }
  }
};

/**
 * 通用表单验证包装器
 * @param {Function} validator - 验证函数
 * @param {Object} formData - 表单数据
 * @param {Function} onSuccess - 验证成功的回调
 * @param {Object} toast - Toast 实例（可选）
 */
export const validateAndSubmit = (validator, formData, onSuccess, toast = null) => {
  const result = validator(formData);
  
  if (result.valid) {
    onSuccess(formData);
  } else {
    if (toast) {
      showValidationErrorsWithToast(result.errors, toast);
    } else {
      showValidationErrors(result.errors);
    }
  }
};
